import { getActiveConversationComments, getTicket, getTicketComments, Model } from '@/common/api/api.pleased';
import {
  ActiveComments,
  ActiveTicket,
  ActiveTicketComment,
  Comment,
  MessageAttachment,
  TicketDetail,
} from '@/common/api/models.pleased';
import { MemberType, MessageType, NoticeType } from '@/common/bot/enum';
import { createMediaMessage, createMessage, createNotice, randomId } from '@/common/bot/lib';
import { BaseMessage } from '@/common/bot/types';
import { autoParse } from '@/common/pleased-parser';
import { sanitizeHTML } from '@/common/utils';
import { useQuery } from '@tanstack/vue-query';
import { computed, ComputedRef } from 'vue';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';
import { devLog } from '@/common/debug';

/**
 * TanStack Query hook to fetch conversation comments for a specific ticket
 * Fetches all comments and ticket details for a given ticket ID
 * @param ticketId - The ticket ID to fetch comments for
 * @param enabled - Whether the query should be enabled
 * @param isClosedOrResolved - If true, uses getTicketComments instead of getActiveConversationComments
 */
export const useConversationComments = (
  ticketId: ComputedRef<number | string | undefined>,
  enabled: ComputedRef<boolean>,
  isClosedOrResolved: ComputedRef<boolean> = computed(() => false)
) => {
  const mockedFlowStore = useMockedFlowStore();
  const { mockedConversation, hasMockedConversation } = storeToRefs(mockedFlowStore);

  return useQuery({
    queryKey: computed(() => ['conversation-comments', ticketId.value, isClosedOrResolved.value]),
    queryFn: async (): Promise<ActiveComments> => {
      if (!ticketId.value) {
        throw new Error('Ticket ID is required');
      }

      // Return mocked conversation data if available in mocked flow
      if (hasMockedConversation.value && mockedConversation.value) {
        devLog.debug('[CONVERSATION_COMMENTS_API] Using mocked conversation data', mockedConversation.value);
        return mockedConversation.value;
      }

      if (isClosedOrResolved.value) {
        // For closed/resolved conversations, use getTicketComments
        const [commentsResponse, ticket] = await Promise.all([
          getTicketComments(Number(ticketId.value)),
          getTicket(Number(ticketId.value)),
        ]);

        const comments = commentsResponse.data;
        return {
          comments: transformCommentsToActiveTicketComments(comments),
          ticket: transformTicketDetailToActiveTicket(ticket),
        };
      }

      // For open conversations, use getActiveConversationComments
      const response = await getActiveConversationComments(Number(ticketId.value));
      return response.data;
    },
    select: (data) => {
      return {
        comments: prepareConversationComments(data.comments),
        ticket: data.ticket,
      };
    },
    enabled: computed(() => !!ticketId.value && enabled.value),
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    retry: true,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 60_000),
    networkMode: 'always',
  });
};

/**
 * Transform Comment[] to ActiveTicketComment[]
 * Used for closed/resolved conversations
 */
const transformCommentsToActiveTicketComments = (comments: Comment[]): ActiveTicketComment[] => {
  return comments.map((comment) => {
    // Determine authorType - typically agents have roleId === 1, but this may vary
    // For now, we'll use a heuristic: if roleId exists and is not 0, it might be an agent
    // Otherwise, default to CUSTOMER. This can be refined based on actual API behavior
    const authorType: 'AGENT' | 'CUSTOMER' = comment.author?.roleId && comment.author.roleId > 0 ? 'AGENT' : 'CUSTOMER';

    // Transform TicketAttachment[] to MessageAttachment[]
    const attachments: MessageAttachment[] | undefined = comment.attachments?.map((att) => ({
      id: String(att.id),
      fileName: att.fileName,
      url: att.url,
      contentType: att.contentType,
    }));

    return {
      commentId: comment.id,
      attachments,
      authorName: comment.author?.name || comment.author?.defaultName || 'Unknown',
      authorType,
      body: comment.body,
      bodyHtml: comment.bodyHtml,
      source: 'LIVE_CHAT' as const, // Default source for closed/resolved conversations
      createAt: comment.createdAt,
    };
  });
};

/**
 * Transform TicketDetail to ActiveTicket
 * Used for closed/resolved conversations
 */
const transformTicketDetailToActiveTicket = (ticket: TicketDetail): ActiveTicket => {
  return {
    id: ticket.id,
    subject: ticket.subject,
    emailSubject: ticket.emailSubject || '',
    status: ticket.status,
    createdAt: ticket.createdAt,
    updatedAt: ticket.updatedAt,
    commentCount: ticket.comments?.length || 0,
  };
};

const prepareConversationComments = (comments: ActiveTicketComment[]) => {
  return comments.flatMap((c) => {
    // The logic can be improved here. We are parsing the HTML of
    // all the comments, instead we should know which comments are safe to parse from BE
    // or the parser should be moved to BE>
    const parsed = c.bodyHtml?.trim() && autoParse(c.bodyHtml);

    let messages: BaseMessage[] = [];
    if (parsed && parsed.length) {
      messages = parsed.map((m) =>
        createMessage({
          type: MessageType.DEFAULT,
          memberType: m.authorType === 'AGENT' ? MemberType.AGENT : MemberType.VISITOR,
          sendToChat: false,
          noSystemPrefix: true,
          message: m.message,
          timestamp: m.timestamp?.valueOf(),
        })
      );
    }

    const sanitizedBodyHtml = sanitizeHTML(c.bodyHtml);

    const message =
      getCommentNotice(c) ||
      createMessage({
        type: MessageType.DEFAULT,
        message: c.authorType === 'AGENT' ? sanitizedBodyHtml : c.body,
        noSystemPrefix: true,
        sendToChat: false,
        timestamp: new Date(c.createAt).getTime(),
        memberType: c.authorType === 'AGENT' ? MemberType.AGENT : MemberType.VISITOR,
      });

    if (!c.attachments?.length) return messages.length ? messages : message;

    const attachmentMessages = c.attachments.map((a) =>
      createMediaMessage({
        attachment: {
          id: randomId(),
          name: a.fileName,
          url: a.url,
          contentType: a.contentType,
          mimeType: a.contentType,
        },
        memberType: c.authorType === 'AGENT' ? MemberType.AGENT : MemberType.VISITOR,
      })
    );
    if (c.body === 'File Upload' || !(message?.message || '').trim() || messages.length)
      return [...messages, ...attachmentMessages];

    return [message, ...attachmentMessages];
  });
};

function getCommentNotice(comment: Model.ActiveTicketComment) {
  if (!(comment.source === 'LIVE_CHAT' || comment.source === 'VOICE_CHAT')) return null;
  if (!comment.body.startsWith('Live chat session') && !comment.body.startsWith('Voice call')) {
    return null;
  }
  return createNotice({
    id: randomId(),
    type: comment.source === 'LIVE_CHAT' ? NoticeType.LIVE_CHAT_SESSION : NoticeType.VOICE_CHAT_SESSION,
    icon: comment.source === 'LIVE_CHAT' ? 'IoChatbubblesSharp' : 'IoCall',
    message: comment.body,
    timestamp: new Date(comment.createAt).getTime(),
  });
}
